import { useQuery } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { fetchMyTargetPerformanceGreenStores } from 'queries/myTarget-performance/performance-metrics'
import { MPMEnvType } from 'utils/env'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { isCompany, isRegion, isGroup } from 'utils/location'
import { TableData } from 'utils/data'

interface GreenStoresFetchProps {
    env: MPMEnvType
    accessToken: string
    location: string
    enabled?: boolean
}

interface GreenStoresResult {
    data: TableData | undefined
    isLoading: boolean
    isError: boolean
    refetch: () => void
}

export const getGreenStoresCardNumber = (location: string): string | null => {
    const { MY_TARGET_PERFORMANCE } = Constants.GREENFIELD_CARDS
    if (isCompany(location) || !location) {
        return MY_TARGET_PERFORMANCE.GREEN_STORES_COMPANY
    }
    if (isRegion(location)) {
        return MY_TARGET_PERFORMANCE.GREEN_STORES_REGION
    }
    if (isGroup(location)) {
        return MY_TARGET_PERFORMANCE.GREEN_STORES_GROUP
    }
    return null
}

export function useMyTargetPerformanceGreenStores({
    env,
    accessToken,
    location,
    enabled = true,
}: GreenStoresFetchProps): GreenStoresResult {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME

    const greenfieldCardNumber = getGreenStoresCardNumber(location)
    const shouldFetch = enabled && greenfieldCardNumber !== null

    const filters = getGreenfieldLocationFilter(
        isCompany(location) ? '' : location,
    )

    const { data, isLoading, isError, refetch } = useQuery(
        [QueryKeys.MY_TARGET_PERFORMANCE_GREEN_STORES, location],
        () =>
            fetchMyTargetPerformanceGreenStores({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber!,
                method: 'GET',
                filters,
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: shouldFetch,
        },
    )

    return { data, isLoading: isLoading && shouldFetch, isError, refetch }
}
